import { Game } from "./Game";
import { GameVars } from "./GameVars";
import { GameConstants } from "./GameConstants";
import { AudioManager } from "./AudioManager";
import { AdvicesManager } from "./scenes/AdvicesManager";
import { SplashState } from "./scenes/splash/SplashState";
import { FootballFieldState } from "./scenes/match-scene/FootballFieldState";
import { PenaltyFootballFieldState } from "./scenes/penalty-scene/PenaltyFootBallFieldState";
import { TrophyState } from "./scenes/level-selection/TrophyState";
import { DailyRewardState } from "./scenes/daily-reward/DailyRewardState";
import { GUI } from "./GUI/GUI";

// https://developers.google.com/gamesnacks/developer/requirements

export class GameManager {

    private static game: Phaser.Game;

    private static lastPlayerTeamID: number;
    private static lastAdversaryTeamID: number;

    public static init(game: Phaser.Game): void {

        GameManager.game = game;

        ytgame.system.onPause(() => {
            if (GUI.currentInstance) {
                GUI.currentInstance.onClickOptionsMenu();
            } 
        });

        ytgame.system.onResume(() => {
            if (GUI.currentInstance) {
                if (GameVars.isTimedLevel) {
                    GUI.currentInstance.game.time.events.resume();
                }
                GUI.currentInstance.hideMenuContainer();
            }
        });

        GameManager.lastPlayerTeamID = null;
        GameManager.lastAdversaryTeamID = null;
        GameVars.tournamentJustUnlocked = false;
        GameVars.matchJustUnlocked = false;

        // por poner algo
        GameVars.playerID = GameConstants.DEFAULT_PLAYER_TEAM_ID;
        GameVars.adversaryID = GameConstants.DEFAULT_ADVERSARY_TEAM_ID;

        GameVars.playerGoals = 3;
        GameVars.adversaryGoals = 3;
        GameVars.gameReadyCalled = false;

        GameManager.readGameData();
    }

    public static startGame(): void {

        ytgame.game.firstFrameReady();

        GameManager.game.state.start("PreLoader", true, false);
    }

    public static onGameAssetsLoaded(): void {

        AudioManager.getInstance().init(GameManager.game);
        AdvicesManager.getInstance().init(this.game);

        GameVars.teamsData = <TeamData[]>GameManager.game.cache.getJSON("teams");
        GameVars.gameText = this.game.cache.getJSON("game_text")[GameConstants.LANGUAGE];

        if (GameVars.gameData.tournamentData.length === 0) {
            // sacar del archivo de edicion de niveles los goles del adversario
            GameManager.setAdversaryGoalsToTournamentData();
            GameManager.writeGameStorage();
        }

        GameVars.currentTournamentName = GameVars.getCurrentTournamentName();

        // entramos por la splash en el caso de que la intro no haya sido vista
        if (GameVars.showDailyRewardScene) {
            GameManager.game.state.start("DailyRewardState");
        } else {

            if (GameVars.gameData.introSeen) {

                GameVars.gameReadyCalled = true;
                ytgame.game.gameReady();

                GameManager.game.state.start("TrophyState");
            } else {
                GameManager.game.state.start("SplashState");
            }
        }  
    }

    public static onIntroSeen(): void {

        GameVars.gameData.introSeen = true;

        GameManager.setGameStorageData(
            GameConstants.GAME_STORAGE_DATA_KEY,
            GameVars.gameData,
            function (): void {
                GameManager.log("Game data stored successfuly");
            }
        );
    }

    public static newGame(): void {

        if (GameVars.showDailyRewardScene) {
            SplashState.currentInstance.goToScene("DailyRewardState");
        } else {
            SplashState.currentInstance.goToScene("TrophyState");
        }
    }

    public static readGameData(): void {

        GameManager.getGameStorageData(GameConstants.GAME_STORAGE_DATA_KEY,
            function (gameData: string): void {

                if (gameData) {

                    GameVars.gameData = JSON.parse(gameData);
                } else {
                    GameVars.gameData = {
                        audioState: [false, false],
                        credits: 0,
                        introSeen: false,
                        showDailyRewardScene: false,
                        dayLastTimePlayed: 0,
                        powerUpState: {
                            freezeGoalkeeperPurchased: false,
                            extraBallsPurchased: false,
                            ballOfFirePurchased: false,
                            voodooPurchased: false
                        },
                        statsData: {
                            scoredGoals: 0,
                            scoredPenalties: 0,
                            matchesPlayed: 0,
                            matchesWon: 0
                        },
                        tournamentData: [],

                    };
                }

                if (GameConstants.DEVELOPMENT) {
                    GameVars.gameData.credits = 50000;
                }

                // PARA LA RECOMPENSA DIARIA
                let datum = new Date();

                if (datum.getDate() !== GameVars.gameData.dayLastTimePlayed && GameVars.gameData.dayLastTimePlayed !== 0) {
                    GameVars.showDailyRewardScene = true;
                } else {
                    GameVars.showDailyRewardScene = false;
                }

                GameVars.gameData.dayLastTimePlayed = datum.getDate();

                GameManager.writeGameStorage();

                GameManager.startGame();
            }
        );
    }

    public static getGameStorageData(key: string, successCb: Function): void {

        if (GameManager.isPlayablesEnv()) {
            ytgame.game.loadData().then((data) => {
                successCb(data);
            });
        } else {
            successCb(localStorage.getItem(key));
        }
    }

    private static setGameStorageData(key: string, value: any, successCb: Function): void {

        if (GameManager.isPlayablesEnv()) {
            ytgame.game.saveData(JSON.stringify(value)).then(() => {
                successCb();
              }, (e) => {
                console.error(e);
              });
        } else {
            localStorage.setItem(key, JSON.stringify(value));
            successCb();
        }
    }

    public static log(text: string, error?: Error): void {

        if (!GameConstants.VERBOSE) {
            return;
        }

        if (error) {
            console.error(text + ":", error);
        } else {
            console.log(text);
        }
    }

    public static onClickPlayDailyRewardState(): void {

        GameManager.game.state.start("TrophyState", true, false);
    }

    public static async showInterstitialAd(): Promise<void> {

        if (!GameManager.isPlayablesEnv()) {
            return;
        }

        const wasMuted = Game.currentInstance.sound.mute;

        try {
            Game.currentInstance.sound.mute = true;
            await ytgame.ads.requestInterstitialAd();
        } catch (error) {
            GameManager.log("failed to show yt interstitial", error as Error);
        } finally {
            Game.currentInstance.sound.mute = wasMuted;
        }
    }

    public static updateAchievedTrophy(): void {

        let tournamentName = GameVars.currentTournamentName;
        let tournamentIndex = GameVars.getTournamentIndexFromTournamentName(tournamentName);

        let tournamentData = GameVars.tournamentDataArray[tournamentIndex];
        tournamentData.won = true;
    }

    public static onNewMatchSelected(tournamentData: TournamentData, matchData: MatchData): void {

        GameVars.showPreMatchLayer = true;

        // TODO esto se podria racionalizar un poco
        let playerID = GameVars.getTeamIDFromTeamName(tournamentData.selectedTeamName);
        let adversaryID = GameVars.getTeamIDFromTeamName(matchData.adversaryName);

        GameVars.tournamentData = tournamentData;

        GameVars.matchID = matchData.id;
        GameVars.matchType = matchData.matchType;
        GameVars.playerID = playerID;
        GameVars.adversaryID = adversaryID;
        GameVars.currentTournamentName = tournamentData.name;
        GameVars.matchJustUnlocked = false;
        GameVars.tournamentJustUnlocked = false;
        GameVars.justUnlockedTournamentName = "";
        GameVars.matchEnded = false;

        GameManager.game.state.start("FootballFieldState", true, false);
    }

    public static onMatchEnded(): void {

        if (GameVars.levelEdition) {
            return;
        }

        GameVars.matchEnded = true;

        const matchData = GameVars.getMatchDataFromMatchID(GameVars.matchID);

        if (GameVars.playerGoals > matchData.goalsPlayer) {
            matchData.goalsPlayer = GameVars.playerGoals;
        }

        // si el siguiente partido esta bloqueado y se ha ganado el presente
        if (GameVars.matchResult === GameConstants.VICTORY) {

            matchData.won = true;

            const nextMatchID = GameVars.getNextMatchIDFromCurrentMatchID(GameVars.matchID);
            const nextMatchData = GameVars.getMatchDataFromMatchID(nextMatchID);

            if (nextMatchData === null) {

                // se ha conseguido la copa del mundo
                GameVars.justUnlockedTournamentName = GameVars.getCurrentTournamentName();
                GameVars.tournamentData.won = true;
                GameVars.tournamentJustUnlocked = true;

            } else {

                if (nextMatchData.locked) {

                    if (matchData.matchType === GameConstants.FINAL_MATCH) {
                        GameVars.justUnlockedTournamentName = GameVars.getCurrentTournamentName();
                        GameVars.tournamentData.won = true;
                        GameVars.tournamentJustUnlocked = true;
                    } else {
                        GameVars.tournamentJustUnlocked = false;
                    }

                    nextMatchData.locked = false;
                    GameVars.matchJustUnlocked = true;

                } else {

                    GameVars.matchJustUnlocked = false;
                }
            }
        }

        GameVars.gameData.statsData.scoredGoals += GameVars.playerGoals;
        GameVars.gameData.statsData.matchesPlayed++;

        if (GameVars.matchResult === GameConstants.VICTORY) {
            GameVars.gameData.statsData.matchesWon++;
        }

        GameManager.writeGameStorage();

        if (FootballFieldState.currentInstance) {

            if (GameVars.matchResult === GameConstants.DRAW) {
                GameManager.game.state.start("PenaltyFootballFieldState", true, false);
            } else {
                FootballFieldState.currentInstance.onMatchEnd();
            }

        } else {
            PenaltyFootballFieldState.currentInstance.onMatchEnd();
        }
    }

    public static showNextTournament(): void {

        GameVars.matchJustUnlocked = false;
        GameVars.tournamentJustUnlocked = false;
        TrophyState.currentInstance.showNextTournament();
    }

    public static showMatchResult(): void {

        GameManager.game.state.start("MatchResultState", true, false);
    }

    public static onPowerUpPurchased(powerUpType: string): void {

        switch (powerUpType) {

            case GameConstants.FREEZE_GOALKEEPER:
                GameVars.gameData.powerUpState.freezeGoalkeeperPurchased = true;
                GameVars.gameData.credits -= GameConstants.FREEZE_GOALKEEPER_PRICE;
                break;
            case GameConstants.EXTRA_BALLS:
                GameVars.gameData.powerUpState.extraBallsPurchased = true;
                GameVars.gameData.credits -= GameConstants.EXTRA_BALLS_PRICE;
                break;
            case GameConstants.BALL_OF_FIRE:
                GameVars.gameData.powerUpState.ballOfFirePurchased = true;
                GameVars.gameData.credits -= GameConstants.BALL_OF_FIRE_PRICE;
                break;
            case GameConstants.VOODOO:
                GameVars.gameData.powerUpState.voodooPurchased = true;
                GameVars.gameData.credits -= GameConstants.VOODOO_PRICE;
                break;
            default:
        }

        GameManager.writeGameStorage();
    }

    public static onPowerUpUsed(powerUpType: string): void {

        switch (powerUpType) {

            case GameConstants.FREEZE_GOALKEEPER:
                GameVars.gameData.powerUpState.freezeGoalkeeperPurchased = false;
                break;
            case GameConstants.EXTRA_BALLS:
                GameVars.gameData.powerUpState.extraBallsPurchased = false;
                break;
            case GameConstants.BALL_OF_FIRE:
                GameVars.gameData.powerUpState.ballOfFirePurchased = false;
                break;
            case GameConstants.VOODOO:
                GameVars.gameData.powerUpState.voodooPurchased = false;
                break;
            default:
        }

        GameManager.writeGameStorage();
    }

    public static updateCreditsAfterAchievedTrophy(): void {

        GameVars.gameData.credits += GameConstants.CREDITS_ACHIEVED_TROPHY;

        GameManager.writeGameStorage();
    }

    public static onClickChangeTeamButton(tournamentName: string): void {

        GameVars.currentTournamentName = tournamentName;

        GameManager.game.state.start("TeamSelectionState", true, false);
    }

    public static onTeamSelected(teamName: string): void {

        const tournamentID = GameVars.getTournamentIndexFromTournamentName(GameVars.currentTournamentName);

        const tournamentData = GameVars.gameData.tournamentData[tournamentID];

        tournamentData.teamSelected = true;
        tournamentData.selectedTeamName = teamName;

        const matchesData = tournamentData.matchesData;

        let changeJoker = false;

        for (let i = 0; i < matchesData.length; i++) {

            if (matchesData[i].adversaryName === teamName) {
                changeJoker = true;
                matchesData[i].adversaryName = tournamentData.jokerTeamName;
            }
        }

        if (changeJoker) {
            tournamentData.jokerTeamName = teamName;
        }

        GameManager.writeGameStorage();

        GameManager.game.state.start("TrophyState", true, false);
    }

    public static onMatchReplayed(): void {

        GameVars.paused = false;

        GameVars.showPreMatchLayer = false;
        GameManager.game.tweens.removeAll();
        GameManager.game.time.events.resume();
    }

    public static updateCreditsAfterMatchEnd(): void {

        if (GameVars.playerGoals > GameVars.adversaryGoals) {
            GameVars.gameData.credits += GameVars.adversaryGoals * GameConstants.CREDITS_GOALS;
            GameVars.gameData.credits += (GameVars.playerGoals - GameVars.adversaryGoals) * GameConstants.CREDITS_EXTRA_GOALS;
        } else {
            GameVars.gameData.credits += GameVars.playerGoals * GameConstants.CREDITS_GOALS;
        }

        GameManager.writeGameStorage();
    }

    public static onDailyRewardItemHit(rewardType: string): void {

        switch (rewardType) {

            case GameConstants.HIGH_CREDITS_REWARD:
                GameVars.gameData.credits += GameConstants.HIGH_DAILY_REWARD_CREDITS;
                DailyRewardState.currentInstance.onCreditsWon(GameConstants.HIGH_DAILY_REWARD_CREDITS);
                break;
            case GameConstants.MEDIUM_CREDITS_REWARD:
                GameVars.gameData.credits += GameConstants.MEDIUM_DAILY_REWARD_CREDITS;
                DailyRewardState.currentInstance.onCreditsWon(GameConstants.MEDIUM_DAILY_REWARD_CREDITS);
                break;
            case GameConstants.LOW_CREDITS_REWARD:
                GameVars.gameData.credits += GameConstants.LOW_DAILY_REWARD_CREDITS;
                DailyRewardState.currentInstance.onCreditsWon(GameConstants.LOW_DAILY_REWARD_CREDITS);
                break;
            case GameConstants.FREEZE_GOALKEEPER_REWARD:
                GameVars.gameData.powerUpState.freezeGoalkeeperPurchased = true;
                DailyRewardState.currentInstance.onPowerUpWon(GameConstants.FREEZE_GOALKEEPER_REWARD);
                break;
            case GameConstants.THREE_EXTRA_BALLS_REWARD:
                GameVars.gameData.powerUpState.extraBallsPurchased = true;
                DailyRewardState.currentInstance.onPowerUpWon(GameConstants.THREE_EXTRA_BALLS_REWARD);
                break;
            case GameConstants.BALL_OF_FIRE_REWARD:
                GameVars.gameData.powerUpState.ballOfFirePurchased = true;
                DailyRewardState.currentInstance.onPowerUpWon(GameConstants.BALL_OF_FIRE_REWARD);
                break;
            case GameConstants.VOODOO_REWARD:
                GameVars.gameData.powerUpState.voodooPurchased = true;
                DailyRewardState.currentInstance.onPowerUpWon(GameConstants.VOODOO_REWARD);
                break;
            case GameConstants.NOTHING_REWARD:
                DailyRewardState.currentInstance.onNothingWon();
                break;
            default:
        }
    }

    public static determineDailyRewardsItems(): string[] {

        // TODO no parece el lugar adecuado pero GameManager tiene que hacerlo de algun modo
        GameVars.showDailyRewardScene = false;

        // meter nada en la mitad de los cofres
        // en la otra mitad meter premios siempre que el jugador no tenga los powerups
        let rewardTypes = [
            GameConstants.HIGH_CREDITS_REWARD,
            GameConstants.MEDIUM_CREDITS_REWARD,
            GameConstants.LOW_CREDITS_REWARD
        ];

        if (!GameVars.gameData.powerUpState.freezeGoalkeeperPurchased) {
            rewardTypes.push(GameConstants.FREEZE_GOALKEEPER_REWARD);
        }

        if (!GameVars.gameData.powerUpState.extraBallsPurchased) {
            rewardTypes.push(GameConstants.THREE_EXTRA_BALLS_REWARD);
        }

        if (!GameVars.gameData.powerUpState.ballOfFirePurchased) {
            rewardTypes.push(GameConstants.BALL_OF_FIRE_REWARD);
        }

        if (!GameVars.gameData.powerUpState.voodooPurchased) {
            rewardTypes.push(GameConstants.VOODOO_REWARD);
        }

        rewardTypes = Phaser.ArrayUtils.shuffle(rewardTypes); // esto para que salgan cosas random pero no repetidas

        let rewards: string[] = [];

        rewards[0] = GameConstants.NOTHING_REWARD;
        rewards[1] = GameConstants.NOTHING_REWARD;
        rewards[2] = GameConstants.NOTHING_REWARD;
        rewards[3] = Math.random() > .5 ? rewardTypes[0] : GameConstants.NOTHING_REWARD;
        rewards[4] = rewardTypes[1];
        rewards[5] = rewardTypes[2];

        rewards = Phaser.ArrayUtils.shuffle(rewards);

        return rewards;
    }

    public static initMatch(): void {

        if (!GameVars.showPreMatchLayer) {
            FootballFieldState.currentInstance.initialize();
            return;
        }

        FootballFieldState.currentInstance.addPreMatchLayer();

        let playerID = GameVars.playerID;
        let adversaryID = GameVars.adversaryID;

        let loader: Phaser.Loader = null;

        if (GameManager.lastPlayerTeamID !== playerID) {

            if (GameManager.lastPlayerTeamID !== null) {
                GameManager.game.cache.removeImage(GameManager.lastPlayerTeamID.toString());
            }

            GameManager.lastPlayerTeamID = playerID;
            loader = GameManager.game.load.atlasXML("" + playerID, GameConstants.ASSETS_DIRECTORY + "/teams/" + playerID + ".png", GameConstants.ASSETS_DIRECTORY + "/teams/" + playerID + ".xml");
            loader.start();
        }

        if (GameManager.lastAdversaryTeamID !== adversaryID) {

            if (GameManager.lastAdversaryTeamID !== null) {
                GameManager.game.cache.removeImage(GameManager.lastAdversaryTeamID.toString());
            }

            GameManager.lastAdversaryTeamID = adversaryID;
            loader = GameManager.game.load.atlasXML("" + adversaryID, GameConstants.ASSETS_DIRECTORY + "/teams/" + adversaryID + ".png", GameConstants.ASSETS_DIRECTORY + "/teams/" + adversaryID + ".xml");
            loader.start();
        }

        if (loader !== null) {
            loader.onLoadComplete.addOnce(GameManager.onTeamTexturesLoaded, GameManager);
        } else {
            GameManager.onTeamTexturesLoaded();
        }
    }

    public static onTeamTexturesLoaded(): void {

        FootballFieldState.currentInstance.initialize();
    }

    public static writeGameStorage(): void {

        GameManager.setGameStorageData(GameConstants.GAME_STORAGE_DATA_KEY, GameVars.gameData,
            () => {
                GameManager.log("Game data stored successfuly");
            }
        );
    }

    private static setAdversaryGoalsToTournamentData(): void {

        GameVars.gameData.tournamentData = GameManager.game.cache.getJSON("tournamentsData");

        for (let i = 0; i < GameVars.gameData.tournamentData.length; i++) {

            let matchesData = GameVars.gameData.tournamentData[i].matchesData;

            for (let j = 0; j < matchesData.length; j++) {

                let matchID = matchesData[j].id;

                let levels = Game.currentInstance.cache.getJSON("levels");

                for (let k = 0; k < levels.length; k++) {

                    if (levels[k][0].level === matchID) {
                        matchesData[j].goalsAdversary = levels[k][0].goals;
                        break;
                    }
                }
            }
        }
    }

    private static isPlayablesEnv(): boolean {

        return typeof ytgame !== "undefined" && ytgame.IN_PLAYABLES_ENV;
    }
}
